import { CSSProperties, Fragment, ReactNode, useState } from 'react';
import { Styles } from 'react-select';

import { DraggableAttributes } from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import styled from 'styled-components';

import { ActionText } from '@components/action-text';
import { BasicFade } from '@components/animators/fade';
import { Badge, BadgeVariant } from '@components/badge';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { useLocalStorageState } from '@components/component-helpers';
import { DragHandleContainer } from '@components/drag-and-drop';
import { useFormContext } from '@components/form';
import { FormLabel } from '@components/form-label';
import { FormDataType, FormInstance } from '@components/form/create-form';
import { FormStoreInstance } from '@components/form/create-form-store';
import { IconButton } from '@components/icon-button';
import { IconFancyContainer, IconFancyContainerProps } from '@components/icon-fancy-container';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconArray } from '@components/icons/array';
import { BaseIcon } from '@components/icons/base';
import { IconBoolean } from '@components/icons/boolean';
import { IconDecimalFloat } from '@components/icons/decimal-float';
import { IconDrag } from '@components/icons/drag';
import { IconDropdown } from '@components/icons/dropdown';
import { IconDropside } from '@components/icons/dropside';
import { IconField } from '@components/icons/field';
import { IconFieldCustom } from '@components/icons/field-custom';
import { IconInput } from '@components/icons/input';
import { IconIntegration } from '@components/icons/integration';
import { IconLabel } from '@components/icons/label';
import { IconList } from '@components/icons/list';
import { IconListDynamic } from '@components/icons/list-dynamic';
import { IconRequired } from '@components/icons/required';
import { IconSend } from '@components/icons/send';
import { IconSubIndicator } from '@components/icons/sub-indicator';
import { IconTextFormatNumber } from '@components/icons/text-format-number';
import { IconTextShort } from '@components/icons/text-short';
import { IconTrash } from '@components/icons/trash';
import { IconVisibilityShown } from '@components/icons/visibility-shown';
import { IconWorkType } from '@components/icons/work-type';
import { Menu } from '@components/menu';
import { Select } from '@components/select';
import { SELECT_ZINDEX } from '@components/select/styles';
import { SquareImage } from '@components/square-image';
import { Stack } from '@components/stack';
import { TabButton } from '@components/tab-button';
import { Text } from '@components/text';
import { Tooltip } from '@components/tooltip';
import { useScopedTranslate } from '@locale/translate';
import { lineClamp } from '@styles/layout-helpers';
import { Color, theme } from '@styles/theme';
import { countTruthy } from '@utils/collections';
import { pxToRem } from '@utils/converters';
import { getKeys } from '@utils/objects';

import { HiddenParameterMapFields, DynamicListParameterMap } from './dynamic-list-parameter-map';
import {
  DefaultValueField,
  ExampleValueField,
  getFieldIds,
  parseParameterMapFieldId,
} from './endpoint-fields';
import { useLists } from './integrations-hooks';
import type {
  Authentication,
  EndpointMethod,
  IntegrationEndpointDateVariable,
  IntegrationEndpointListVariable,
  IntegrationEndpointSelectVariable,
  IntegrationEndpointVariable,
  List,
  SelectValueType,
} from './integrations-types';
import {
  ClosedDrawerInner,
  ClosedDrawerRow,
  DrawerArrow,
  DrawerField,
  DrawerSelectSlot,
  Field,
  FIELD_PADDING,
  FieldIndentBorder,
  FieldIndentBorderContainer,
  Grid,
  TYPE_ICON_MAP,
} from './styles';

export const endpointColorMap: Record<EndpointMethod, Color> = {
  POST: 'elementBlue100',
  GET: 'elementGreen100',
  PATCH: 'elementOrange100',
  PUT: 'indigo100',
  DELETE: 'elementRed100',
};

export const AuthTypeMap: Record<Authentication['authType'], string> = {
  '': 'None',
  basic: 'Basic',
  bearer_token: 'Bearer token',
  oauth2: 'OAuth2',
  api_key: 'API key',
  authorization_header: 'Authorization header',
  jwt_token: 'JWT token',
  oauth_connect: 'OAuth',
};

export const CUSTOM_FIELD_PREFIX = '_custom_';

// A plain object is torn into name__key fragments when the value is stored, so it can't
// be kept as one custom value. An array is stored whole, so it is fine.
export const isUnstorableCustomValueShape = (result: unknown): boolean => {
  return typeof result === 'object' && result !== null && !Array.isArray(result);
};

const DRAWER_TRANSITION = { duration: 0.3, ease: 'easeInOut' } as const;

const methodTagColorMap: Record<EndpointMethod, BadgeVariant> = {
  POST: 'blue',
  GET: 'green',
  PATCH: 'orange',
  PUT: 'indigo',
  DELETE: 'red',
};

type VisibleColumnsType = {
  name: boolean;
  type: boolean;
  label: boolean;
  example: boolean;
  default: boolean;
  visible: boolean;
  required: boolean;
  format: boolean;
};

export const MethodTag = ({ method }: { method: EndpointMethod }) => (
  <Badge variant={methodTagColorMap[method]} data-testid="method" text={method} />
);

type ClampedTextProps = React.ComponentProps<typeof Text>;

export const ClampedText = (props: ClampedTextProps) => {
  return (
    <Text
      {...props}
      style={{
        ...lineClamp('-webkit-box', 1, true),
        paddingRight: pxToRem(6),
        ...props.style,
      }}
    />
  );
};

export const methodFancyContainerColorMap: Record<
  EndpointMethod,
  IconFancyContainerProps['variant']
> = {
  POST: 'blue',
  GET: 'green',
  PATCH: 'orange',
  PUT: 'indigo',
  DELETE: 'red',
};

type DraggableIconProps = {
  variant: IconFancyContainerProps['variant'];
  icon: ReactNode;
  dragAttributes?: DraggableAttributes;
  dragListeners?: SyntheticListenerMap;
};

export const DraggableIcon = ({
  variant,
  icon,
  dragAttributes,
  dragListeners,
}: DraggableIconProps) => {
  return (
    <DragHandleContainer
      {...dragAttributes}
      {...dragListeners}
      $isDraggable={Boolean(dragListeners)}
    >
      <IconFancyContainer
        variant={variant}
        icon={dragListeners ? icon : <IconDrag />}
        hoverIcon={dragListeners ? <IconDrag /> : undefined}
      />
    </DragHandleContainer>
  );
};

const parseArrayOfStrings = (value: unknown): string[] | null | undefined => {
  const items = typeof value === 'string' ? value.split('\n') : value;
  if (!Array.isArray(items)) return undefined;
  const result = items
    .map((item) => (typeof item === 'string' ? item : item?.value ?? '').trim())
    .filter(Boolean);
  return result.length > 0 ? result : null;
};

const getFormEndpointVariable = ({
  formStore,
  allVariables,
  variable,
  parent,
  visibleColumns = {
    name: true,
    type: true,
    label: true,
    example: false,
    default: false,
    visible: false,
    required: false,
    format: false,
  },
}: {
  formStore: FormStoreInstance;
  allVariables: IntegrationEndpointVariable[];
  variable: IntegrationEndpointVariable;
  parent?: IntegrationEndpointVariable;
  visibleColumns?: VisibleColumnsType;
}) => {
  const formValues = formStore.getState().values;
  const name = parent ? generateTemplateName(allVariables, variable) : variable.name;

  const {
    typeId,
    optionsFromId,
    valueTypeId,
    labelId,
    exampleId,
    defaultValueId,
    visibleId,
    requiredId,
    formatId,
  } = getFieldIds(name);
  const type = formValues[typeId] ?? variable.type;
  const label = formValues[labelId];
  const example = formValues[exampleId];
  const defaultValue = formValues[defaultValueId];
  const visible = formValues[visibleId];
  const required = formValues[requiredId];
  const format = formValues[formatId];

  const isArrayOfStrings = type === 'array_of_strings';
  const processedExample = isArrayOfStrings ? parseArrayOfStrings(example) : example;
  const processedDefaultValue = isArrayOfStrings ? parseArrayOfStrings(defaultValue) : defaultValue;
  const getRequiredValue = () => {
    if (type === 'boolean') return false;
    return required ?? variable.required;
  };
  const requiredValue = getRequiredValue();

  const isSecretType = type === 'secret';
  const hasOptionsField = type === 'select' || type === 'array_of_strings';
  const variableOptionsFrom = (variable as IntegrationEndpointListVariable).options_from;
  const variableValueType = (variable as IntegrationEndpointListVariable).value_type;
  const hasList = hasOptionsField && Boolean(formValues[optionsFromId] ?? variableOptionsFrom);
  const valueType = hasList
    ? ((formValues[valueTypeId] ?? variableValueType) as SelectValueType | undefined)
    : undefined;

  const variableData = {
    name: variable.name,
    type: visibleColumns.type ? type ?? variable.type : variable.type,
    label: visibleColumns.label ? label ?? variable.label : variable.label,
    example: visibleColumns.example
      ? processedExample !== undefined
        ? processedExample
        : variable.example
      : variable.example,
    default_value: visibleColumns.default
      ? processedDefaultValue !== undefined
        ? processedDefaultValue
        : variable.default_value
      : variable.default_value,
    // Secret variables are always hidden and never required.
    visible: isSecretType
      ? false
      : visibleColumns.visible
      ? visible ?? variable.visible
      : variable.visible,
    ...(!isSecretType && requiredValue && { required: true }),
    ...(variable.jqQuery && { jqQuery: variable.jqQuery }),
    ...(type === 'date' && {
      format: visibleColumns.format
        ? format ?? (variable as IntegrationEndpointDateVariable).format
        : (variable as IntegrationEndpointDateVariable).format,
    }),
    ...(hasOptionsField && {
      options_from: visibleColumns.type
        ? formValues[optionsFromId] ?? variableOptionsFrom
        : variableOptionsFrom,
      ...getParameterMapField(variable, formValues),
    }),
    ...(hasOptionsField && valueType && valueType !== 'text' && { value_type: valueType }),
    ...(type === 'array' &&
      'output_format' in variable &&
      variable.output_format && { output_format: variable.output_format }),
  } as IntegrationEndpointVariable;
  if (variable.template) {
    variableData.template = variable.template.map((templateVariable) => {
      return getFormEndpointVariable({
        formStore,
        allVariables,
        variable: templateVariable,
        parent: variable,
        visibleColumns,
      }) as IntegrationEndpointVariable;
    });
  }

  return variableData;
};

const collectParameterMap = (
  formValues: Record<string, any>,
  variableName: string
): Record<string, string> | null => {
  const map: Record<string, string> = {};
  let hasField = false;
  for (const [fieldId, value] of Object.entries(formValues)) {
    const parsed = parseParameterMapFieldId(fieldId);
    if (!parsed || parsed.variableName !== variableName) continue;
    hasField = true;
    if (typeof value === 'string' && value) {
      map[parsed.parameterName] = value;
    }
  }
  // null when this variable has no parameter fields, so the saved map is kept.
  return hasField ? map : null;
};

// The `parameter_map` part of a select variable, or {} when there's nothing to map.
const getParameterMapField = (
  variable: IntegrationEndpointVariable,
  formValues: Record<string, any>
): { parameter_map?: Record<string, string> } => {
  const current = collectParameterMap(formValues, variable.name);
  const map = current ?? (variable as IntegrationEndpointSelectVariable).parameter_map;
  return map && Object.keys(map).length > 0 ? { parameter_map: map } : {};
};

export const getFormEndpointVariables = ({
  formStore,
  endpointVariables,
  visibleColumns = {
    name: true,
    type: true,
    label: true,
    example: false,
    default: false,
    visible: false,
    required: false,
    format: false,
  },
}: {
  formStore: FormStoreInstance;
  endpointVariables: IntegrationEndpointVariable[];
  visibleColumns?: VisibleColumnsType;
}) => {
  const variables = endpointVariables.map((variable) => {
    return getFormEndpointVariable({
      formStore,
      allVariables: endpointVariables,
      variable,
      visibleColumns,
    });
  });
  return variables;
};

const flattenAllVariables = (
  allVariables: IntegrationEndpointVariable[],
  parentPrefix?: string
) => {
  const flat: { variable: IntegrationEndpointVariable; flatten: string }[] = [];

  allVariables.forEach((variable) => {
    const flattenName = parentPrefix
      ? `${parentPrefix}${nestedSeparator}${variable.name}`
      : variable.name;
    flat.push({ variable: variable, flatten: flattenName });
    if (variable.template) {
      flat.push(...flattenAllVariables(variable.template, flattenName));
    }
  });

  return flat;
};

export const nestedSeparator = '__';
export const generateTemplateName = (
  allVariables: IntegrationEndpointVariable[],
  template: IntegrationEndpointVariable
) => {
  const flattenVariableList = flattenAllVariables(allVariables);
  return (
    flattenVariableList.find((flatten) => flatten.variable === template)?.flatten ?? template.name
  );
};

type CustomValueSidebarOptions = {
  exampleData?: any;
  onOpenCustomValueSidebar?: (path: string[], scopedData: any, existingValue?: any) => void;
};

type FieldTableProps<T> = {
  endpointId: string;
  endpointVariables: IntegrationEndpointVariable[];
  form: FormInstance<T>;
  visibleColumns?: VisibleColumnsType;
  showWebhookUrl?: boolean;
  showArrayType?: boolean;
  handleRemoveField?: (name: string) => void;
  requestType?: string;
  customValueOptions?: CustomValueSidebarOptions;
  fieldColumnLabel?: string;
  listTemplateId?: string;
  showValueType?: boolean;
};

const FieldTableHeaderItem = ({
  text,
  icon: Icon,
  iconSize = 16,
  tooltip,
}: {
  text?: string;
  icon?: typeof BaseIcon;
  iconSize?: number;
  tooltip?: string;
}) => {
  return (
    <Field>
      <Tooltip content={tooltip}>
        <Stack space={1} orientation="horizontal" align="center">
          {Icon && <Icon color="elementPurple50" size={iconSize} />}
          {text && <Text text={text} font="semibold14" color="textSecondary" />}
        </Stack>
      </Tooltip>
    </Field>
  );
};

export const FieldTable = <T extends FormDataType>({
  endpointId,
  endpointVariables,
  showWebhookUrl = false,
  showArrayType = false,
  visibleColumns = {
    name: true,
    type: true,
    label: true,
    example: true,
    default: false,
    visible: false,
    required: false,
    format: false,
  },
  handleRemoveField,
  requestType,
  customValueOptions,
  fieldColumnLabel,
  listTemplateId,
  showValueType = true,
  ...rest
}: FieldTableProps<T>) => {
  const { t } = useScopedTranslate(locale);
  const { data: lists } = useLists(listTemplateId ? { templateId: listTemplateId } : undefined);

  const [collapsedArrays, setCollapsedArrays] = useLocalStorageState<{ [key: string]: string[] }>({
    key: 'integration-arrays-collapsed',
    defaultValue: {},
  });
  const updateCollapsed = (field: string) => {
    const isCollapsed = (collapsedArrays[endpointId] ?? []).includes(field);
    const newCollapsed = { ...collapsedArrays };
    if (isCollapsed) {
      newCollapsed[endpointId] = newCollapsed[endpointId].filter((name) => name !== field);
      if (newCollapsed[endpointId].length === 0) {
        delete newCollapsed[endpointId];
      }
    } else if (!newCollapsed[endpointId]) {
      newCollapsed[endpointId] = [field];
    } else {
      newCollapsed[endpointId].push(field);
    }
    setCollapsedArrays(newCollapsed);
  };
  const smallColumnKeys = new Set<keyof VisibleColumnsType>(['visible', 'required', 'format']);
  const numColumns = getKeys(visibleColumns).filter(
    (key) => !smallColumnKeys.has(key) && visibleColumns[key]
  ).length;

  const numSmallColumns = countTruthy(
    visibleColumns.visible,
    visibleColumns.required,
    handleRemoveField
  );

  return (
    <Grid $numColumns={numColumns} $numSmallColumns={numSmallColumns}>
      {visibleColumns.name && (
        <FieldTableHeaderItem text={fieldColumnLabel ?? t('field')} icon={IconField} />
      )}
      {visibleColumns.type && <FieldTableHeaderItem text={t('type')} icon={IconWorkType} />}
      {visibleColumns.label && <FieldTableHeaderItem text={t('label')} icon={IconLabel} />}
      {visibleColumns.example && <FieldTableHeaderItem text={t('testRequest')} icon={IconSend} />}
      {visibleColumns.default && <FieldTableHeaderItem text={t('defaultValue')} icon={IconInput} />}
      {handleRemoveField && <FieldTableHeaderItem />}
      {visibleColumns.visible && (
        <FieldTableHeaderItem
          icon={IconVisibilityShown}
          iconSize={24}
          tooltip={t('visibleTooltip')}
        />
      )}
      {visibleColumns.required && (
        <FieldTableHeaderItem icon={IconRequired} iconSize={24} tooltip={t('requiredTooltip')} />
      )}
      {endpointVariables.map((field) => {
        const cannotBeDeleted = field.type === 'array' && endpointVariables.length === 1;
        return (
          <FieldVariable
            key={field.name + field.type}
            allFields={endpointVariables}
            field={field}
            lists={lists ?? []}
            visibleColumns={visibleColumns}
            handleRemoveField={handleRemoveField}
            showWebhookUrl={showWebhookUrl}
            showArrayType={showArrayType}
            collapsedArrays={collapsedArrays[endpointId] ?? []}
            updateCollapsed={updateCollapsed}
            cannotBeDeleted={cannotBeDeleted}
            requestType={requestType}
            customValueOptions={customValueOptions}
            showValueType={showValueType}
            currentPath={[]}
            {...rest}
          />
        );
      })}
    </Grid>
  );
};

type FieldVariableProps<T> = {
  form: FormInstance<T>;
  visibleColumns: VisibleColumnsType;
  showWebhookUrl?: boolean;
  showArrayType?: boolean;
  handleRemoveField?: (name: string) => void;
  allFields: IntegrationEndpointVariable[];
  field: IntegrationEndpointVariable;
  lists: List[];

  overwriteName?: string;
  arrayDepth?: number;
  isFirstVariable?: boolean;
  isLastVariableArray?: boolean[];
  collapsedArrays: string[];
  updateCollapsed: (field: string) => void;
  cannotBeDeleted?: boolean;
  requestType?: string;
  customValueOptions?: CustomValueSidebarOptions;
  currentPath?: string[];
  showValueType?: boolean;
};

const FieldVariable = <T extends FormDataType>({
  allFields,
  field,
  lists,
  showWebhookUrl = false,
  showArrayType = false,
  visibleColumns,
  handleRemoveField,
  overwriteName,
  arrayDepth,
  isFirstVariable,
  isLastVariableArray,
  collapsedArrays,
  updateCollapsed,
  cannotBeDeleted,
  requestType,
  customValueOptions,
  currentPath = [],
  ...rest
}: FieldVariableProps<T>) => {
  const exampleData = customValueOptions?.exampleData;
  const onOpenCustomValueSidebar = customValueOptions?.onOpenCustomValueSidebar;
  const { t } = useScopedTranslate(locale);
  const isCollapsed = collapsedArrays.includes(field.name);
  const fieldName = overwriteName ?? field.name;

  const getScopedDataForArray = () => {
    if (!exampleData) return null;

    // Navigate to the current level in exampleData
    let scopedData = exampleData;

    for (const pathPart of currentPath) {
      scopedData = scopedData?.[pathPart];
      // If we've navigated to an array (template level), use first item for further navigation
      if (Array.isArray(scopedData) && scopedData.length > 0) {
        scopedData = scopedData[0];
      }
    }

    // Use the original field name (overwriteName) when looking up in example data
    // because field.name may have path prefix (e.g., 'users__devices') while
    // the actual data property is just 'devices'
    const lookupName = overwriteName ?? field.name;
    const arrayData = scopedData?.[lookupName];

    if (Array.isArray(arrayData) && arrayData.length > 0) {
      // Return the first item if it's an array
      return arrayData[0];
    }

    return null;
  };

  const handleAddCustomValue = () => {
    if (onOpenCustomValueSidebar) {
      const scopedData = getScopedDataForArray();
      if (scopedData) {
        // Use original field name (overwriteName) for the path, not the prefixed template name
        const newPath = [...currentPath, overwriteName ?? field.name];
        onOpenCustomValueSidebar(newPath, scopedData);
      }
    }
  };

  const variableName =
    field.type === 'array' ? (
      <Stack orientation="horizontal" space={2} align="center" justify="space-between">
        <Button
          variant="secondary"
          iconLeft={<IconArray />}
          text={`${fieldName.toUpperCase()} • ${(field.template ?? []).length}`}
          iconRight={isCollapsed ? <IconDropside /> : <IconDropdown />}
          onClick={() => updateCollapsed(field.name)}
        />
        {exampleData && onOpenCustomValueSidebar && getScopedDataForArray() && (
          <Menu
            buttonProps={{
              iconLeft: <IconAddPlus />,
              variant: 'secondary',
              iconRight: <IconDropdown />,
              size: 'sm',
            }}
          >
            <Menu.Item
              icon={<IconFieldCustom />}
              label={t('addCustomValue')}
              onSelect={handleAddCustomValue}
            />
          </Menu>
        )}
      </Stack>
    ) : overwriteName ? (
      <Text text={overwriteName} font="regular14" ellipsis tooltip={overwriteName} />
    ) : undefined;
  const shouldShowArrayItems = field.type === 'array' && !isCollapsed;

  return (
    <>
      <FieldRow
        field={field}
        allFields={allFields}
        lists={lists}
        visibleColumns={visibleColumns}
        handleRemoveField={handleRemoveField}
        showWebhookUrl={showWebhookUrl}
        showArrayType={showArrayType}
        overwriteName={variableName}
        arrayDepth={arrayDepth}
        isFirstVariable={isFirstVariable}
        isLastVariableArray={isLastVariableArray}
        isCollapsed={isCollapsed}
        cannotBeDeleted={cannotBeDeleted}
        requestType={requestType}
        exampleData={exampleData}
        onOpenCustomValueSidebar={onOpenCustomValueSidebar}
        currentPath={currentPath}
        {...rest}
      />
      {shouldShowArrayItems &&
        field.template?.map((templateField, index) => {
          const templateFieldName = generateTemplateName(allFields, templateField);
          const depth = (arrayDepth ?? -1) + 1;
          const isLast = index === (field.template ?? []).length - 1;

          const lastVariablesArray = [...(isLastVariableArray ?? []), isLast];
          // Use overwriteName (original field name) for path, not field.name (which may be prefixed)
          const newPath = [...currentPath, overwriteName ?? field.name];

          return (
            <FieldVariable
              key={templateField.name}
              allFields={allFields}
              field={{ ...templateField, name: templateFieldName }}
              lists={lists}
              visibleColumns={visibleColumns}
              handleRemoveField={handleRemoveField}
              showWebhookUrl={showWebhookUrl}
              showArrayType={showArrayType}
              overwriteName={templateField.name}
              arrayDepth={depth}
              isFirstVariable={index === 0}
              isLastVariableArray={lastVariablesArray}
              collapsedArrays={collapsedArrays}
              updateCollapsed={updateCollapsed}
              requestType={requestType}
              customValueOptions={customValueOptions}
              currentPath={newPath}
              {...rest}
            />
          );
        })}
    </>
  );
};

const FieldRowBorderIndent = ({
  hasTemplate,
  depth,
  isFirst = false,
  isLastVariableArray = [],
  isCollapsed = false,
  hasDrawer = false,
  containerStyle,
}: {
  hasTemplate: boolean;
  depth?: number;
  isFirst?: boolean;
  isLastVariableArray?: boolean[];
  isCollapsed?: boolean;
  hasDrawer?: boolean;
  containerStyle?: CSSProperties;
}) => {
  const arrayBorderColors = [
    theme.color.uiTrans20,
    theme.color.blueTrans20,
    theme.color.greenTrans20,
    theme.color.orangeTrans20,
  ];
  if (depth === undefined) {
    return null;
  }
  const isLastVariableEmptyArrayOrOther = !hasTemplate || isCollapsed;

  return (
    <FieldIndentBorderContainer $depth={depth} style={containerStyle}>
      {[...Array(depth + 1)].map((_, index) => {
        // Line ends only if all items from index to depth are last
        const isLastVariable =
          !hasDrawer &&
          isLastVariableArray.slice(index, depth + 1).every(Boolean) &&
          isLastVariableEmptyArrayOrOther;
        return (
          <FieldIndentBorder
            key={index}
            $color={arrayBorderColors[index % arrayBorderColors.length]}
            $isFirst={index === depth && isFirst}
            $isLast={isLastVariable}
          />
        );
      })}
    </FieldIndentBorderContainer>
  );
};

const dropdownMenuStyles: Styles<any, boolean> = {
  menuPortal: (provided) => ({
    ...provided,
    zIndex: SELECT_ZINDEX,
    '@media (max-width: 1900px)': {
      minWidth: '320px',
    },
  }),
};

type FieldRowProps<T> = {
  field: IntegrationEndpointVariable;
  allFields: IntegrationEndpointVariable[];
  lists: List[];
  form: FormInstance<T>;
  showWebhookUrl?: boolean;
  showArrayType?: boolean;
  visibleColumns: VisibleColumnsType;
  handleRemoveField?: (name: string) => void;
  overwriteName?: JSX.Element;
  arrayDepth?: number;
  isFirstVariable?: boolean;
  isLastVariableArray?: boolean[];
  isCollapsed?: boolean;
  cannotBeDeleted?: boolean;
  requestType?: string;
  exampleData?: any;
  onOpenCustomValueSidebar?: (path: string[], scopedData: any, existingValue?: any) => void;
  currentPath?: string[];
  showValueType?: boolean;
};

export const useFieldTypeLabel = () => {
  const { t } = useScopedTranslate(locale);
  return (type: IntegrationEndpointVariable['type']): string => t(type);
};

export const useFieldTypes = (excluded: Array<string> = []) => {
  const { t } = useScopedTranslate(locale);
  return Object.entries(TYPE_ICON_MAP)
    .filter(([type, _]) => !excluded.includes(type))
    .map(([type, icon]) => ({
      label: (
        <Tooltip placement="left" content={type === 'secret' ? t('secretTypeTooltip') : undefined}>
          <Stack orientation="horizontal" align="center" space={2}>
            {icon}
            {t(type as IntegrationEndpointVariable['type'])}
          </Stack>
        </Tooltip>
      ),
      value: type as IntegrationEndpointVariable['type'],
    }));
};

export const FieldMinHeightWrapper = styled.div({
  height: pxToRem(40),
  display: 'flex',
  alignItems: 'center',
});

const ValueTypeSelector = ({
  value,
  onChange,
}: {
  value: SelectValueType;
  onChange: (value: SelectValueType) => void;
}) => {
  const { t } = useScopedTranslate(locale);
  return (
    <TabButton<SelectValueType>
      size="md"
      value={value}
      onChange={onChange}
      options={[
        { value: 'text', label: t('valueTypeString'), icon: <IconTextShort /> },
        { value: 'number', label: t('valueTypeNumber'), icon: <IconTextFormatNumber /> },
        { value: 'decimal', label: t('valueTypeDecimal'), icon: <IconDecimalFloat /> },
        { value: 'boolean', label: t('valueTypeBoolean'), icon: <IconBoolean /> },
      ]}
    />
  );
};

const useListDrawer = ({
  field,
  fieldType,
  formValues,
  optionsFromId,
  valueTypeId,
  lists,
}: {
  field: IntegrationEndpointVariable;
  fieldType: IntegrationEndpointVariable['type'];
  formValues: Record<string, unknown>;
  optionsFromId: string;
  valueTypeId: string;
  lists: List[];
}) => {
  const listVariable = field as IntegrationEndpointListVariable;
  const isListType = fieldType === 'select' || fieldType === 'array_of_strings';
  const isOptionalList = fieldType === 'array_of_strings';
  const listShortId =
    (formValues[optionsFromId] as string | undefined) ?? listVariable.options_from;
  const currentValueType =
    (formValues[valueTypeId] as SelectValueType | undefined) ?? listVariable.value_type ?? 'text';

  const selectedList = lists.find((list) => list.id === listShortId);
  const isDynamic = selectedList?.sync?.isDynamic ?? false;
  const parameters = isDynamic ? selectedList?.sync?.parameters ?? [] : [];
  const parameterMap = collectParameterMap(formValues, listVariable.name) ?? {};
  const isMapped = parameters.every((parameter) => parameterMap[parameter.name]);

  const [isManuallyOpen, setIsManuallyOpen] = useState(false);
  // A bound dynamic list must have every parameter mapped before it can close,
  // whichever field type it is; an array with no list picked stays optional.
  const canClose = listShortId ? isMapped : isOptionalList;

  return {
    variable: listVariable,
    isListType,
    isOptionalList,
    listShortId,
    valueType: currentValueType,
    listName: selectedList?.name,
    isDynamic,
    isMapped,
    parameters,
    isOpen: isListType && (!canClose || isManuallyOpen),
    canClose,
    open: () => setIsManuallyOpen(true),
    close: () => setIsManuallyOpen(false),
  };
};

const FieldRow = <T extends FormDataType>({
  field,
  allFields,
  lists = [],
  form: Form,
  showWebhookUrl = false,
  showArrayType = false,
  visibleColumns,
  handleRemoveField,
  overwriteName,
  arrayDepth,
  isFirstVariable = false,
  isLastVariableArray,
  isCollapsed,
  cannotBeDeleted = false,
  requestType,
  exampleData,
  onOpenCustomValueSidebar,
  currentPath = [],
  showValueType = true,
}: FieldRowProps<T>) => {
  const { t } = useScopedTranslate(locale);
  const {
    typeId,
    optionsFromId,
    valueTypeId,
    labelId,
    exampleId,
    defaultValueId,
    visibleId,
    requiredId,
    formatId,
    jqQueryId,
  } = getFieldIds(field.name);
  const { store } = useFormContext();
  const formValues = store.useFormValues();
  const fieldType = (formValues[typeId] as IntegrationEndpointVariable['type']) ?? field.type;
  const isArray = field.type === 'array' && (field.template ?? []).length > 0;
  const isSecretType = fieldType === 'secret';

  const drawer = useListDrawer({
    field,
    fieldType,
    formValues,
    optionsFromId,
    valueTypeId,
    lists,
  });
  // The drawer configures the field's type, so only show it where the type column is.
  const hasDrawer = visibleColumns.type && drawer.isListType;
  const excludeTypes = [
    ...(!showArrayType && !isArray ? ['array'] : []),
    ...(!showWebhookUrl ? ['webhookUrl'] : []),
  ];

  const types = useFieldTypes(excludeTypes);

  const isCustomResponseValue = Boolean(field.jqQuery);

  const handleEditCustomValue = () => {
    if (isCustomResponseValue && onOpenCustomValueSidebar && exampleData) {
      // Use the EXACT SAME algorithm as getScopedDataForArray
      let scopedData = exampleData;

      for (const pathPart of currentPath) {
        scopedData = scopedData?.[pathPart];
        // If we've navigated to an array (template level), use first item for further navigation
        if (Array.isArray(scopedData) && scopedData.length > 0) {
          scopedData = scopedData[0];
        }
      }

      // Extract the original field name if this is a nested field
      // Flattened names look like: items__{custom_field_name}
      // We need just the last part: {custom_field_name}
      const originalName =
        currentPath.length > 0
          ? field.name.split(`${currentPath.join('__')}__`)[1] || field.name
          : field.name;

      const fieldWithOriginalName = {
        ...field,
        name: originalName,
      };

      onOpenCustomValueSidebar(currentPath, scopedData, fieldWithOriginalName);
    }
  };

  // Clear the now-stale default when the list changes.
  const listChanged = drawer.isListType && drawer.listShortId !== drawer.variable.options_from;
  const defaultValue = listChanged
    ? ''
    : fieldType === field.type
    ? field.default_value
    : undefined;
  const valueField = { ...field, type: fieldType } as IntegrationEndpointVariable;
  const formats = [
    {
      value: 'DDMMYYYY',
      label: 'DD MM YYYY',
    },
    {
      value: 'MMDDYYYY',
      label: 'MM DD YYYY',
    },
    {
      value: 'YYYYMMDD',
      label: 'YYYY MM DD',
    },
  ];
  const displayName =
    typeof overwriteName === 'string'
      ? overwriteName
      : field.name.includes(CUSTOM_FIELD_PREFIX)
      ? field.name.split('__').find((part) => part.startsWith(CUSTOM_FIELD_PREFIX)) || field.name
      : field.name;

  return (
    <Fragment key={field.name}>
      {visibleColumns.name && (
        <Field $depth={arrayDepth} $noBorderBottom={hasDrawer}>
          <FieldRowBorderIndent
            hasTemplate={(field.template ?? []).length > 0}
            depth={arrayDepth}
            isFirst={isFirstVariable}
            isLastVariableArray={isLastVariableArray}
            isCollapsed={isCollapsed}
            hasDrawer={hasDrawer}
            containerStyle={hasDrawer ? { height: `calc(100% + ${FIELD_PADDING}px)` } : undefined}
          />
          <FieldMinHeightWrapper>
            {overwriteName && !isCustomResponseValue ? (
              overwriteName
            ) : isCustomResponseValue ? (
              <ActionText
                font="regular14"
                color="elementIndigo100"
                hoverColor="elementIndigo100"
                onClick={handleEditCustomValue}
                iconLeft={<IconFieldCustom size={16} />}
                text={displayName}
                title={displayName}
                ellipsis
              />
            ) : (
              <Text text={field.name} font="regular14" ellipsis tooltip={field.name} />
            )}
          </FieldMinHeightWrapper>
        </Field>
      )}
      {field.jqQuery && <Form.HiddenField id={jqQueryId} defaultValue={field.jqQuery} />}
      {visibleColumns.type && (
        <Field $noBorderBottom={hasDrawer}>
          <>
            <Form.Select
              label=""
              id={typeId}
              options={types}
              defaultValue={field.type}
              isClearable={false}
              isDisabled={isArray || (field.type === 'file' && requestType === 'file')}
              styles={dropdownMenuStyles}
            />
            {fieldType === 'date' && visibleColumns.format && (
              <Form.Select
                label=""
                id={formatId}
                options={formats}
                defaultValue={(field as IntegrationEndpointDateVariable).format ?? 'YYYYMMDD'}
                isClearable={false}
                styles={dropdownMenuStyles}
              />
            )}
          </>
        </Field>
      )}
      {visibleColumns.label && (
        <Field $noBorderBottom={hasDrawer}>
          <Form.TextField id={labelId} label="label" hideLabel defaultValue={field.label} />
        </Field>
      )}
      {visibleColumns.example && (
        <Field $noBorderBottom={hasDrawer}>
          <ExampleValueField
            id={exampleId}
            field={valueField}
            form={Form}
            defaultValue={field.example}
          />
        </Field>
      )}
      {visibleColumns.default && (
        <Field $noBorderBottom={hasDrawer}>
          <DefaultValueField
            key={`${defaultValueId}-${fieldType}-${field.default_value}-${
              drawer.listShortId ?? ''
            }`}
            id={defaultValueId}
            field={valueField}
            form={Form}
            defaultValue={defaultValue}
          />
        </Field>
      )}
      {handleRemoveField && (
        <Field $noBorderBottom={hasDrawer}>
          <FieldMinHeightWrapper>
            <IconButton
              icon={<IconTrash />}
              onClick={() => handleRemoveField(field.name)}
              variant="ghost-purple"
              disabled={cannotBeDeleted}
            />
          </FieldMinHeightWrapper>
        </Field>
      )}
      {visibleColumns.visible && (
        <Field $noBorderBottom={hasDrawer}>
          <FieldMinHeightWrapper>
            <Form.Switch
              key={`${visibleId}-${isSecretType}`}
              id={visibleId}
              label={field.name}
              labelVisible={false}
              defaultValue={isSecretType ? false : field.visible ?? true}
              isDisabled={isSecretType}
            />
          </FieldMinHeightWrapper>
        </Field>
      )}
      {visibleColumns.required &&
        (fieldType === 'boolean' ? (
          <Field $noBorderBottom={hasDrawer}>
            <></>
          </Field>
        ) : (
          <Field $noBorderBottom={hasDrawer}>
            <FieldMinHeightWrapper data-testid={`integration-required-toggle-${field.name}`}>
              <Form.Switch
                key={`${requiredId}-${isSecretType}`}
                id={requiredId}
                label={field.name}
                labelVisible={false}
                defaultValue={isSecretType ? false : field.required ?? false}
                isDisabled={isSecretType}
              />
            </FieldMinHeightWrapper>
          </Field>
        ))}
      {hasDrawer && (
        <DrawerField $depth={arrayDepth}>
          <FieldRowBorderIndent
            hasTemplate={false}
            depth={arrayDepth}
            isLastVariableArray={isLastVariableArray}
          />
          {/* Always registered so the value survives collapse/expand and the
              dirty baseline stays the original stored value. */}
          <Form.HiddenField id={optionsFromId} defaultValue={drawer.variable.options_from} />
          {showValueType && (
            <Form.HiddenField
              id={valueTypeId}
              defaultValue={drawer.variable.value_type ?? 'text'}
            />
          )}
          {drawer.isListType && (
            <HiddenParameterMapFields field={drawer.variable} parameters={drawer.parameters} />
          )}
          {drawer.isOpen ? (
            <BasicFade transition={DRAWER_TRANSITION}>
              <Card variant="gray" space={2}>
                <Stack orientation="horizontal" align="flex-end" space={4}>
                  <DrawerArrow>
                    <IconSubIndicator color="elementPurple50" />
                  </DrawerArrow>
                  <DrawerSelectSlot>
                    <FormLabel text={t('listLabel')} />
                    <Select
                      options={lists.map((list) => ({ value: list.id, label: list.name }))}
                      value={drawer.listShortId ?? ''}
                      onChange={(value) => {
                        store.actions.setValue(optionsFromId, value || undefined);
                        // Clear the now-stale default (its options came from the old list).
                        store.actions.setValue(defaultValueId, '');
                        // Stay open after picking a list so the value type is still reachable.
                        drawer.open();
                      }}
                      isClearable={drawer.isOptionalList}
                      placeholder={t('selectListPlaceholder')}
                      hideLabel
                      styles={dropdownMenuStyles}
                    />
                  </DrawerSelectSlot>
                  {showValueType && drawer.listShortId && (
                    <Stack space={1}>
                      <FormLabel text={t('sendValueAs')} info={t('valueTypeTooltip')} />
                      <ValueTypeSelector
                        value={drawer.valueType}
                        onChange={(value) => store.actions.setValue(valueTypeId, value)}
                      />
                    </Stack>
                  )}
                  <Button
                    text={t('close')}
                    variant="primary-blue"
                    isDisabled={!drawer.canClose}
                    onClick={drawer.close}
                  />
                </Stack>
                {drawer.isListType && (
                  <DynamicListParameterMap
                    field={drawer.variable}
                    allFields={allFields}
                    parameters={drawer.parameters}
                    keepOpen={drawer.open}
                  />
                )}
              </Card>
            </BasicFade>
          ) : (
            <ClosedDrawerRow type="button" onClick={drawer.open}>
              <IconSubIndicator color="elementPurple50" />
              <ClosedDrawerInner>
                {drawer.isDynamic ? (
                  <IconListDynamic color="elementPurple50" />
                ) : (
                  <IconList color="elementPurple50" />
                )}
                <Text
                  font="semibold12"
                  color="textSecondary"
                  ellipsis
                  text={
                    drawer.listShortId
                      ? drawer.listName ?? t('listUnavailable')
                      : t('manualInputHint')
                  }
                />
              </ClosedDrawerInner>
            </ClosedDrawerRow>
          )}
        </DrawerField>
      )}
    </Fragment>
  );
};

export const getVariableCount = (variables?: IntegrationEndpointVariable[]) => {
  return flattenAllVariables(variables ?? []).length;
};

export const getSecretVariableDefaultValueIds = (
  variables: IntegrationEndpointVariable[]
): string[] => {
  return flattenAllVariables(variables)
    .filter(({ variable }) => variable.type === 'secret')
    .map(({ flatten }) => getFieldIds(flatten).defaultValueId as string);
};

const locale = {
  en: {
    array: 'Object array',
    array_of_strings: 'String array',
    field: 'Field',
    type: 'Type',
    label: 'Label',
    testRequest: 'Test request',
    defaultValue: 'Default value',
    text: 'Single line text',
    paragraph: 'Long text',
    date: 'Date',
    email: 'Email',
    phone: 'Phone number',
    number: 'Number (Integer)',
    select: 'Select',
    file: 'File',
    boolean: 'Boolean',
    decimal: 'Decimal (Float)',
    webhookUrl: 'Unique webhook url',
    json: 'JSON',
    addCustomValue: 'Add a custom list value',
    secret: 'Secret',
    visibleTooltip:
      'Visibility of the field in the action sidebar. When off, the default value is used. For sensitive values like tokens or passwords, use the "Secret" type.',
    requiredTooltip:
      "Whether the field is required in the action sidebar. When on, the action can't be saved without a value.",
    secretTypeTooltip:
      'Secret type is for sensitive values like tokens or passwords. Stored securely, hidden from the action sidebar, and redacted in request logs.',
    optionalListPlaceholder: 'Optional list',
    selectListPlaceholder: 'Select list...',
    listLabel: 'List',
    sendValueAs: 'Send value as',
    close: 'Close',
    manualInputHint: 'No list — values are entered manually',
    listUnavailable: 'List unavailable',
    valueTypeTooltip:
      'List values are sent as a string by default. Pick a different type if the integration expects the value as a number, decimal or boolean.',
    valueTypeString: 'String',
    valueTypeNumber: 'Number',
    valueTypeDecimal: 'Decimal',
    valueTypeBoolean: 'Boolean',
  },
};

export const generateFieldName = () => {
  return `${CUSTOM_FIELD_PREFIX}${Date.now().toString(36)}`;
};

type IntegrationIconProps = {
  src?: string | null;
  size?: 'sm' | 'md' | 'lg';
};

export const IntegrationIcon = ({ src, size }: IntegrationIconProps) => {
  return src ? <SquareImage src={src} size={size} /> : <IconIntegration />;
};

export type IntegrationItem<Data extends { shortId: string; order?: number | null }> =
  | { id: string; type: 'endpoint'; data: Data }
  | { id: string; type: 'webhook'; data: Data };

export const combineEndpointsAndWebhooks = <
  Endpoint extends { shortId: string; order?: number | null },
  Webhook extends { shortId: string; order?: number | null }
>(
  endpoints: Endpoint[],
  webhooks: Webhook[]
): IntegrationItem<Endpoint | Webhook>[] => {
  const endpointItems: IntegrationItem<Endpoint | Webhook>[] = endpoints.map((endpoint) => ({
    id: endpoint.shortId,
    type: 'endpoint',
    data: endpoint,
  }));
  const webhookItems: IntegrationItem<Endpoint | Webhook>[] = webhooks.map((webhook) => ({
    id: webhook.shortId,
    type: 'webhook',
    data: webhook,
  }));
  return [...endpointItems, ...webhookItems].sort(
    (itemA, itemB) => (itemA.data.order ?? 0) - (itemB.data.order ?? 0)
  );
};
